import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { SquarePen, Check } from "lucide-react";

import CustomInput from "../../../components/UI/PageInput/CustomInput";
import usePatchData from "../../../CustomHooks/useAxios/usePatchData";
import Modal from "../../../components/portal/Modal";
import ErrorDisplay from "../../../components/UI/ErrorDisplay";
import GradientButton from "../../../components/UI/buttonUI/GradientButton";
import {
  UPDATE_MATERIAL_TRANSPORT,
  MATERIAL_TRANSPORT_SETUP,
} from "../apiEndpoints";
import { nonNegativeNumberValidation } from "../../../validations/formValidations";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import SearchDropdown from "../../../components/UI/PageInput/SearchDropdown";
import DateInput from "../../../components/UI/PageInput/DateInput";
import useGetData from "../../../CustomHooks/useAxios/useGetData";
import LoadingDisplay from "../../../components/UI/LoadingDisplay";
import { handleErrMessage } from "../../../functions/handleErrMessage";
import { checkNotNull } from "../../../functions/checkValue";

function UpdateMaterialTransport({ material, closeDialog }) {
  const {
    handleSubmit,
    setValue,
    control,
    watch,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      material_id: material.material.id,
      quantityRequired: checkNotNull(material.quantityRequired),
      quantityDispatchedFromFactory: checkNotNull(
        material.quantityDispatchedFromFactory,
      ),
      quantityReceivedAtSite: checkNotNull(material.quantityReceivedAtSite),
      quantityDispatchedFromSite: checkNotNull(
        material.quantityDispatchedFromSite,
      ),
      quantityReceivedAtFactory: checkNotNull(
        material.quantityReceivedAtFactory,
      ),
      ft_id: material.fieldTechnician.id,
      dateOfTransport: material.dateOfTransport,
      vehicleNumber: material.vehicleNumber,
      driverName: material.driverName,
      remarks: material.remarks,
    },
  });
  const { data, isLoaded, error, getData } = useGetData();
  const { executePatch, loading, successResponse, formError, setFormError } =
    usePatchData();

  const navigate = useNavigate();
  const { dateOfTransport, material_id } = watch();

  // Update Material API Call
  const updateMaterial = (data) => {
    executePatch(data, `${UPDATE_MATERIAL_TRANSPORT}/${material.id}`);
  };

  useEffect(() => {
    getData(MATERIAL_TRANSPORT_SETUP);
  }, []);

  return (
    <Modal
      onClose={loading || successResponse ? undefined : closeDialog}
      modalWidth={`${
        successResponse ? "max-w-sm" : "max-w-2xl h-[85vh] overflow-auto"
      } w-11/12 max-sm:w-[95%] max-md:p-4 pb-8 pt-7 px-8`}
    >
      <>
        {successResponse ? (
          <div className="text-center">
            <Check color="green" className="mx-auto" size={30} />
            <p className="py-5">Successfully Updated!</p>
            <GradientButton
              btnText="Ok"
              width="w-26"
              onClick={() => navigate(0)}
            />
          </div>
        ) : isLoaded && !error ? (
          <>
            {formError && (
              <ErrorDisplay
                message={formError}
                closeAction={() => setFormError("")}
              />
            )}
            <h2 className="text-lg font-figtreeBold mb-3">
              Update {material.material.name} material
            </h2>
            <form
              onSubmit={
                !loading ? (e) => handleSubmit(updateMaterial)(e) : undefined
              }
            >
              <div className="flex gap-3 w-full">
                <div className="w-full">
                  <SearchDropdown
                    name="material_id"
                    label="Material *"
                    keyName="name"
                    id="id"
                    options={data.materials}
                    setValue={setValue}
                    control={control}
                    selectedVal={material_id}
                    error={errors.material_id}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    type="number"
                    name="quantityRequired"
                    labelText="Required Quantity"
                    placeholder="Enter"
                    register={{
                      ...register("quantityRequired", {
                        ...nonNegativeNumberValidation,
                      }),
                    }}
                    error={errors.quantityRequired}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-full">
                  <CustomInput
                    type="number"
                    name="quantityDispatchedFromFactory"
                    labelText="Quantity Dispatched from Factory"
                    placeholder="Enter"
                    register={{
                      ...register("quantityDispatchedFromFactory", {
                        ...nonNegativeNumberValidation,
                      }),
                    }}
                    error={errors.quantityDispatchedFromFactory}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    type="number"
                    name="quantityReceivedAtSite"
                    labelText="Quantity Received at Site"
                    placeholder="Enter"
                    register={{
                      ...register("quantityReceivedAtSite", {
                        ...nonNegativeNumberValidation,
                      }),
                    }}
                    error={errors.quantityReceivedAtSite}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-full">
                  <CustomInput
                    type="number"
                    name="quantityDispatchedFromSite"
                    labelText="Quantity Dispatched from Site"
                    placeholder="Enter"
                    register={{
                      ...register("quantityDispatchedFromSite", {
                        ...nonNegativeNumberValidation,
                      }),
                    }}
                    error={errors.quantityDispatchedFromSite}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    type="number"
                    name="quantityReceivedAtFactory"
                    labelText="Quantity Received at Factory"
                    placeholder="Enter"
                    register={{
                      ...register("quantityReceivedAtFactory", {
                        ...nonNegativeNumberValidation,
                      }),
                    }}
                    error={errors.quantityReceivedAtFactory}
                  />
                </div>
              </div>
              <div className="flex gap-3 w-full">
                <div className="w-full">
                  <SearchDropdown
                    name="ft_id"
                    label="Field Technician *"
                    keyName="fullName"
                    id="id"
                    options={data.fieldTechnicians}
                    setValue={setValue}
                    control={control}
                    selectedVal={watch("ft_id")}
                    error={errors.ft_id}
                    search={true}
                  />
                </div>
                <div className="w-full">
                  <DateInput
                    label="Transport Date *"
                    name={"dateOfTransport"}
                    value={dateOfTransport}
                    setValue={setValue}
                    register={{
                      ...register("dateOfTransport", {
                        required: "Please add transport date",
                      }),
                    }}
                    error={errors.dateOfTransport}
                    disableFutureDays={true}
                  />
                </div>
              </div>
              <div className="flex gap-3 w-full">
                <div className="w-full">
                  <CustomInput
                    type="text"
                    name="vehicleNumber"
                    labelText="Vehicle Number *"
                    placeholder="Enter"
                    register={{
                      ...register("vehicleNumber", {
                        required: "Please enter vehicle number",
                      }),
                    }}
                    error={errors.vehicleNumber}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    type="text"
                    name="driverName"
                    labelText="Driver Name *"
                    placeholder="Enter"
                    register={{
                      ...register("driverName", {
                        required: "Please enter driver name",
                      }),
                    }}
                    error={errors.driverName}
                  />
                </div>
              </div>
              <CustomInput
                type="text"
                name="remarks"
                labelText="Remark"
                placeholder="Ente"
                register={{
                  ...register("remarks"),
                }}
              />
              <div className="flex-center-align pt-2 gap-2">
                <PrimaryButton
                  btnText={!loading ? "Update" : "Updating..."}
                  loading={loading}
                  Icon={SquarePen}
                  size={18}
                />
                <GrayButton
                  disabled={loading}
                  btnText="Cancel"
                  width="w-40"
                  onClick={closeDialog}
                />
              </div>
            </form>
          </>
        ) : error ? (
          <ErrorDisplay message={handleErrMessage(error)} />
        ) : (
          <LoadingDisplay />
        )}
      </>
    </Modal>
  );
}

export default UpdateMaterialTransport;
